import type { BrandAssets } from "@/entities/brand-asset";
import type {
  OverlayConfig as OverlayConfigV6,
  OverlayItem as OverlayItemV6,
} from "../../../api/posterTemplateService";

/**
 * 「加品牌」产出的 stamped 版本:把已叠上去的 logo / 二维码还原成画面调整里**可拖的现有元素**(1006 演示机复测)。
 *
 * stamped 版本的 baseUrl 是已烙品牌的图,api 保存/预览重新合成时用的是回溯后的 origin 素图(asset.editBaseUrl)。
 * 编辑器拿素图当底、把这里还原出的元素叠上去,所见即合成所得:改位置 = 挪走原 logo,不会出现「旧 logo 还在 + 新 logo」。
 *
 * 位置来源(api 透出的 currentOverlayConfig 是 stamp 形状,不是 V6):
 *  - `{logo:{x,y,w,h}}`(军师微调链给的归一化 bbox)→ 直接用;
 *  - `{logo:{position}}` / 缺省 → ai stamp 用的模板 tpl_a1 锚点(logo 左上 12% 边距 24px;码右下 18% 边距 32px)
 *    按真实像素换算。logo 高度按品牌库登记的宽高比估算(SQUARE/WIDE/TALL),与服务端差几个像素,保存后以编辑器摆放为准。
 *
 * currentOverlayConfig 已是 V6(version:1)= 用户在本版本上存过画面调整,那份才是真相 → 返回 null,走常规读法。
 */

/** ai templates.yaml tpl_a1(stamp-brand 端点默认模板)的锚点。 */
const STAMP_ANCHORS = {
  logo: { position: "top-left", sizeRatio: 0.12, paddingPx: 24 },
  qr: { position: "bottom-right", sizeRatio: 0.18, paddingPx: 32 },
} as const;

const ASPECT: Record<string, number> = { SQUARE: 1, WIDE: 3, TALL: 0.5 };

export interface StampedBrandInfo {
  logo?: boolean | null;
  qr?: boolean | null;
  logoRef?: string | null;
  qrRef?: string | null;
  logoUploadId?: string | null;
  qrCodeMode?: "UPLOAD" | "URL" | null;
  qrCodeUploadId?: string | null;
  qrCodeUrl?: string | null;
}

interface StampSource {
  stampedBrand?: unknown;
  currentOverlayConfig?: unknown;
  actualWidth?: number | null;
  actualHeight?: number | null;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function bboxOf(v: unknown): OverlayItemV6 | null {
  const o = asRecord(v);
  if (!o) return null;
  const x = num(o.x);
  const y = num(o.y);
  const w = num(o.w);
  const h = num(o.h);
  if (x == null || y == null || w == null || h == null) return null;
  return { x, y, w, h };
}

/** compositor `_resolve_position` 同款九宫格(归一化)。 */
function place(position: string, w: number, h: number, padX: number, padY: number) {
  const col = position.endsWith("left") ? "l" : position.endsWith("right") ? "r" : "c";
  const row = position.startsWith("top") ? "t" : position.startsWith("bottom") ? "b" : "m";
  const x = col === "l" ? padX : col === "r" ? 1 - w - padX : (1 - w) / 2;
  const y = row === "t" ? padY : row === "b" ? 1 - h - padY : (1 - h) / 2;
  return { x: Math.max(0, x), y: Math.max(0, y) };
}

function pickByRef<T extends { id: string; name: string; isDefault: boolean }>(
  list: T[] | undefined,
  ref: string | null | undefined
): T | null {
  if (!list || list.length === 0) return null;
  if (ref) {
    const hit = list.find((x) => x.id === ref) ?? list.find((x) => x.name === ref);
    if (hit) return hit;
  }
  return list.find((x) => x.isDefault) ?? list[0];
}

export function stampedOverlayOf(
  asset: StampSource,
  brandAssets: BrandAssets | undefined,
  canvasAspect: number
): OverlayConfigV6 | null {
  const info = asRecord(asset.stampedBrand) as StampedBrandInfo | null;
  if (!info) return null;
  const raw = asRecord(asset.currentOverlayConfig);
  if (raw && raw.version === 1) return null;

  const W = asset.actualWidth && asset.actualWidth > 0 ? asset.actualWidth : 1000;
  const H =
    asset.actualHeight && asset.actualHeight > 0 ? asset.actualHeight : W / (canvasAspect || 1);
  const out: OverlayConfigV6 = { version: 1, logo: null, qr: null };

  if (info.logo !== false) {
    const entry = info.logoUploadId
      ? (brandAssets?.logos.find((l) => l.objectKey === info.logoUploadId) ?? null)
      : pickByRef(brandAssets?.logos, info.logoRef);
    const key = info.logoUploadId ?? entry?.objectKey ?? null;
    if (key) {
      const cfg = asRecord(raw?.logo);
      let item = bboxOf(cfg);
      if (!item) {
        const w = num(cfg?.size_ratio) ?? STAMP_ANCHORS.logo.sizeRatio;
        const aspect = ASPECT[entry?.aspect ?? "SQUARE"] ?? 1;
        const h = (w * W) / H / aspect;
        const pad = num(cfg?.padding) ?? STAMP_ANCHORS.logo.paddingPx;
        const pos = typeof cfg?.position === "string" ? cfg.position : STAMP_ANCHORS.logo.position;
        item = { ...place(pos, w, h, pad / W, pad / H), w, h };
      }
      out.logo = { ...item, rotation: 0 };
      out.logoUploadId = key;
    }
  }

  if (info.qr !== false) {
    let mode = info.qrCodeMode ?? null;
    let uploadId = info.qrCodeUploadId ?? null;
    let url = info.qrCodeUrl ?? null;
    if (!mode) {
      const entry = pickByRef(brandAssets?.qrs, info.qrRef);
      mode = entry?.mode ?? null;
      uploadId = entry?.mode === "UPLOAD" ? (entry.objectKey ?? null) : null;
      url = entry?.mode === "URL" ? (entry.url ?? null) : null;
    }
    if ((mode === "UPLOAD" && uploadId) || (mode === "URL" && url)) {
      const cfg = asRecord(raw?.qrcode ?? raw?.qr);
      let item = bboxOf(cfg);
      if (!item) {
        const w = num(cfg?.size_ratio) ?? STAMP_ANCHORS.qr.sizeRatio;
        const h = (w * W) / H; // 码物理方形
        const pad = num(cfg?.padding) ?? STAMP_ANCHORS.qr.paddingPx;
        const pos = typeof cfg?.position === "string" ? cfg.position : STAMP_ANCHORS.qr.position;
        item = { ...place(pos, w, h, pad / W, pad / H), w, h };
      }
      out.qr = item;
      out.qrCodeMode = mode;
      out.qrCodeUploadId = mode === "UPLOAD" ? uploadId : null;
      out.qrCodeUrl = mode === "URL" ? url : null;
    }
  }
  return out;
}
